<template>
  <div class="h-full flex flex-col bg-[#f8fafc] gap-6 overflow-auto">

    <!-- Store Request Form Card -->
    <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden border-t-4 border-t-[#0d4169]">
      <div class="px-6 pt-5 pb-1 flex justify-between items-center gap-4 print-hide">
        <h2 class="text-base font-bold text-[#0d4169] text-left m-0">Store Requisition (SR)</h2>
        <div class="flex items-center gap-4">
          <span v-if="loading || centralLoading" class="text-xs text-slate-400 animate-pulse text-right">Loading data...</span>
          <div class="flex gap-4">
            <button
              type="button"
              @click="switchRequestTab('store')"
              :class="activeRequestTab === 'store'
                ? 'border-[#0d4169] text-[#0d4169]'
                : 'border-transparent text-slate-500'"
              class="pb-1 text-sm font-bold border-b-2 hover:text-[#0d4169] transition-colors whitespace-nowrap"
            >
              Store Request
            </button>
            <button
              type="button"
              @click="switchRequestTab('central')"
              :class="activeRequestTab === 'central'
                ? 'border-[#0d4169] text-[#0d4169]'
                : 'border-transparent text-slate-500'"
              class="pb-1 text-sm font-bold border-b-2 hover:text-[#0d4169] transition-colors whitespace-nowrap"
            >
              Central Request
            </button>
          </div>
        </div>
      </div>
      <form v-if="activeRequestTab === 'store'" @submit.prevent="processRequest" class="px-6 pb-4 pt-2 print-hide">
        <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">

          <!-- SR Number (display-only) -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">SR Number</label>
            <input
              :value="srNumberDisplay || '-'"
              type="text"
              readonly
              class="px-2.5 py-1.5 border border-[#e2e8f0] rounded-md bg-slate-50 text-slate-600 font-semibold text-sm cursor-not-allowed"
            >
          </div>

          <!-- Transaction Date -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Transaction Date</label>
            <input v-model="form.date" type="date"
              :min="todayDate"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm" required>
          </div>

          <!-- Department (Dropdown from User's Assigned Departments) -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Requesting Department</label>
            <select 
              v-model="form.requestedDepartment"
              @change="handleDeptChange"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
              required
            >
              <option value="" disabled>Select Department</option>
              <option v-for="dept in userDeptList" :key="dept" :value="dept">{{ dept }}</option>
            </select>
          </div>

          <!-- Category -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Select Category</label>
            <select v-model="form.categoryId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer">
              <option :value="null" disabled>Select Category</option>
              <option v-for="cat in selectableCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
            </select>
          </div>

          <!-- Item -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Select Item</label>
            <select v-model="form.itemId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer" required>
              <option :value="null" disabled>Select Item</option>
              <option v-for="item in filteredItems" :key="item.id" :value="item.id">{{ item.itemName }} ({{ item.unit }})</option>
            </select>
          </div>

          <!-- Quantity Requested -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Quantity Requested</label>
            <input  v-model.number="form.quantity" type="number" min="1"    step="any"
            inputmode="decimal" placeholder="0"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm" required>
          </div>

          <!-- Remark -->
          <div class="flex flex-col gap-1 text-left col-span-2">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Remark</label>
            <input v-model="form.remark" type="text" placeholder="e.g. For project X..."
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm">
          </div>

        </div>

        <!-- Action Buttons -->
        <div class="mt-3 pt-3 pr-8 border-t border-slate-100 flex justify-end gap-3">
          <button v-if="isEditing" type="button" @click="cancelEdit"
            class="px-5 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md hover:bg-slate-300 transition-all">Cancel</button>
          <button type="submit"
            class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95">
            {{ isEditing ? 'Update Item' : 'Add to List' }}
          </button>
        </div>
      </form>

      <!-- Central Request form (same fields; different dropdown sources) -->
      <form v-else @submit.prevent="processCentralRequest" class="px-6 pb-4 pt-2 print-hide">
        <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">SR Number</label>
            <input
              :value="srNumberDisplay || '-'"
              type="text"
              readonly
              class="px-2.5 py-1.5 border border-[#e2e8f0] rounded-md bg-slate-50 text-slate-600 font-semibold text-sm cursor-not-allowed"
            >
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Transaction Date</label>
            <input v-model="centralForm.date" type="date"
              :min="todayDate"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm" required>
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Requesting Department</label>
            <select
              v-model="centralForm.departmentId"
              @change="handleCentralDeptChange"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
              required
            >
              <option :value="null" disabled>Select Department</option>
              <option
                v-for="dept in centralRequestingDepartments"
                :key="dept.id"
                :value="dept.id"
              >
                {{ dept.displayLabel }}
              </option>
            </select>
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Select Category</label>
            <select
              :value="centralForm.categoryId ?? ''"
              @change="handleCentralCategoryChange"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
              <option value="" disabled>Select Category</option>
              <option v-for="cat in centralCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
            </select>
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Select Item</label>
            <select
              v-model="centralForm.itemKey"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
              required
            >
              <option value="" disabled>Select Item</option>
              <option
                v-for="item in centralItems"
                :key="`${item.type}_${item.id}`"
                :value="`${item.type}_${item.id}`"
              >
                {{ formatCentralItemOptionLabel(item) }}
              </option>
            </select>
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Quantity Requested</label>
            <input v-model.number="centralForm.quantity" type="number" min="1" step="any"
              inputmode="decimal" placeholder="0"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm" required>
          </div>

          <div class="flex flex-col gap-1 text-left col-span-2">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Remark</label>
            <input v-model="centralForm.remark" type="text" placeholder="e.g. For project X..."
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm">
          </div>
        </div>

        <div class="mt-3 pt-3 pr-8 border-t border-slate-100 flex justify-end gap-3">
          <button v-if="isCentralEditing" type="button" @click="cancelCentralEdit"
            class="px-5 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md hover:bg-slate-300 transition-all">Cancel</button>
          <button type="submit"
            class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95">
            {{ isCentralEditing ? 'Update Item' : 'Add to List' }}
          </button>
        </div>
      </form>
    </div>

    <!-- Request List Table -->
    <div v-if="activeRequestTab === 'store' && requestList.length > 0" class="flex flex-col gap-2">

      <div class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <!-- Print Header (Hidden unless printing) -->
        <div class="hidden print:block mb-6">
          <div class="print-doc-header print-doc-header-grid">
            <div class="print-doc-left">
              <div class="print-company-block">
                <img
                  v-if="printCompanyLogo"
                  :src="printCompanyLogo"
                  :alt="printCompanyName || 'Company Logo'"
                  class="print-company-logo"
                />
                <div v-else class="print-company-logo-fallback">Logo</div>
              </div>
            </div>
            <div class="print-doc-title-wrap">
              <div class="print-company-name print-company-name-title">{{ printCompanyName }}</div>
              <h1 class="print-doc-title text-xl font-bold text-center mb-2">Store Requisition (SR)</h1>
            </div>
            <div class="print-doc-meta print-doc-right">
              <div><strong>Date:</strong> {{ new Date().toLocaleDateString() }}</div>
            </div>
          </div>
          <hr class="my-4 border-black" />
        </div>

        <table class="w-full border-collapse bg-white overflow-hidden print-table">
          <thead>
            <tr>
              <th>#</th>
              <!-- <th>Req No</th> -->
              <th>Date</th>
              <th>Department</th>
              <th>Category</th>
              <th>Item</th>
              <th>Qty</th>
              <th>Remark</th>
              <th class="print-hide">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(req, index) in requestList" :key="index" class="hover:bg-slate-100/50 transition-colors border-b">
              <td>{{ index + 1 }}</td>
              <!-- <td>{{ req.reqNo }}</td> -->
              <td>{{ req.date }}</td>
              <td>{{ req.requestedDepartment || currentUser?.role || '-' }}</td>
              <td>{{ getCategoryName(req.categoryId) }}</td>
              <td>{{ getItemName(req.itemId) }}</td>
              <td>{{ req.quantity }}</td>
              <td>{{ req.remark || '-' }}</td>
              <td class="print-hide">
                <div class="flex items-center gap-1">
                  <button @click="editItem(index)" class=" editbtn hover:underline text-emerald-600">Edit</button>
                  <button @click="deleteItem(index)" class="delbtn p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                   Delete
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Print Footer (Hidden unless printing) -->
        <div class="hidden print:block mt-8 w-full">
          <div class="flex flex-col gap-1.5 pl-8">
            <div class="flex items-baseline gap-2 text-sm border-t border-black pt-1 mt-1">
              <span class="font-black text-[#0d4169]">Total Items:</span>
              <span class="font-black text-base text-[#0d4169]">{{ totalItems }}</span>
            </div>
          </div>

          <div class="mt-16 flex justify-between px-10 text-sm">
            <div class="text-center">
              <div class="border-b border-black w-40 mb-2"></div>
              <strong>Prepared By</strong>
            </div>
            <div class="text-center">
              <div class="border-b border-black w-40 mb-2"></div>
              <strong>Checked By</strong>
            </div>
            <div class="text-center">
              <div class="border-b border-black w-40 mb-2"></div>
              <strong>Authorized By</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary + Save Button (Moved Below Table) -->
      <div class="flex justify-between items-center px-2 pt-4 print-hide">
        <div class="text-left">
          <p class="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Total Items</p>
          <p class="text-sm font-black text-[#0d4169]">{{ totalItems }}</p>
        </div>
        <button @click="saveToDatabase" :disabled="isSaving"
          class="px-5 py-1.5 bg-emerald-600 text-white text-sm font-bold rounded-md shadow-md hover:bg-emerald-700 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2">
          <div
  v-if="isSaving"
  class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
></div>
        <span v-if="!isSaving">Save Requisition</span> 
        </button>
      </div>

    </div>

    <!-- Central Request List Table (local list only; save flow later) -->
    <div v-if="activeRequestTab === 'central' && centralRequestList.length > 0" class="flex flex-col gap-2">
      <div class="w-full overflow-x-auto shadow-sm rounded-xl">
        <table class="w-full border-collapse bg-white overflow-hidden">
          <thead>
            <tr>
              <th>#</th>
              <th>Date</th>
              <th>Department</th>
              <th>Category</th>
              <th>Item</th>
              <th>Qty</th>
              <th>Remark</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(req, index) in centralRequestList" :key="index" class="hover:bg-slate-100/50 transition-colors border-b">
              <td>{{ index + 1 }}</td>
              <td>{{ req.date }}</td>
              <td>{{ getCentralDeptLabel(req.departmentId) }}</td>
              <td>{{ getCentralCategoryName(req.categoryId) }}</td>
              <td>{{ req.itemName || '-' }}</td>
              <td>{{ req.quantity }}</td>
              <td>{{ req.remark || '-' }}</td>
              <td>
                <div class="flex items-center gap-1">
                  <button type="button" @click="editCentralItem(index)" class="editbtn hover:underline text-emerald-600">Edit</button>
                  <button type="button" @click="deleteCentralItem(index)" class="delbtn p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex justify-between items-center px-2 pt-4">
        <div class="text-left">
          <p class="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Total Items</p>
          <p class="text-sm font-black text-[#0d4169]">{{ centralRequestList.length }}</p>
        </div>
        <button
          type="button"
          @click="saveCentralToDatabase"
          :disabled="isCentralSaving"
          class="px-5 py-1.5 bg-emerald-600 text-white text-sm font-bold rounded-md shadow-md hover:bg-emerald-700 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
        >
          <div
            v-if="isCentralSaving"
            class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
          ></div>
          <span v-if="!isCentralSaving">Save Requisition</span>
        </button>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import store from '../../store'
import { apiFetch } from '../../utils/api'

// Tabs: Store Request is default
const activeRequestTab = ref('store')

// Dropdowns
const categories = ref([])
const registeredItems = ref([])
const departments = ref([])

// Central Request dropdowns (backend-aggregated)
const centralRequestingDepartments = ref([])
const centralCategories = ref([])
const centralItems = ref([])
const centralLoading = ref(false)

// State
const loading = ref(false)
const isSaving = ref(false)
const isEditing = ref(false)
const editIndex = ref(-1)
const isCentralEditing = ref(false)
const centralEditIndex = ref(-1)
const isCentralSaving = ref(false)

// Display-only SR number fetched from backend.
// This is intentionally NOT sent in the save/update payload.
const srNumberDisplay = ref('')

// Form
const getInitialForm = () => ({
  date: new Date().toISOString().slice(0, 10),
  departmentId: null,
  categoryId: null,
  itemId: null,
  quantity: 0,
  remark: '',
  requestedDepartment: '',
})
const form = ref(getInitialForm())

const getInitialCentralForm = () => ({
  date: new Date().toISOString().slice(0, 10),
  departmentId: null,
  categoryId: null,
  itemKey: '',
  quantity: 0,
  remark: '',
})
const centralForm = ref(getInitialCentralForm())
const todayDate = new Date().toISOString().slice(0, 10)

// Context
const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)
const userId = computed(() => currentUser.value?.id || null)
const printCompanyName = computed(() =>
  currentUser.value?.company?.name ||
  currentUser.value?.companyName ||
  currentUser.value?.company_name ||
  'Company'
)
const printCompanyLogo = computed(() =>
  currentUser.value?.company?.logo_url ||
  currentUser.value?.companyLogo ||
  currentUser.value?.company_logo ||
  ''
)

const selectableCategories = computed(() => categories.value)

const totalItems = computed(() => requestList.value.length)

// Filter
const filteredItems = computed(() => {
  if (!form.value.categoryId) return []
  return registeredItems.value
    .filter(item => item.categoryId === form.value.categoryId)
    .slice()
    .sort((a, b) => String(a?.itemName || '').localeCompare(String(b?.itemName || ''), undefined, { sensitivity: 'base' }))
})

const userDeptList = computed(() => {
  // Parse privilege — may be a JSON string or an object
  const rawPriv = currentUser.value?.privilege
  let privilege = {}
  if (rawPriv) {
    try {
      privilege = typeof rawPriv === 'string' ? JSON.parse(rawPriv) : rawPriv
    } catch { privilege = {} }
  }
  const hasAll = privilege?.all === true || privilege?.All === true

  // If user has All privilege, show every fetched department
  if (hasAll) {
    return departments.value.map(d => d.name).filter(Boolean)
  }

  // Otherwise show only the departments assigned to this user
  const deptNames = new Set()

  // 1. Add primary department name if available
  const primaryId = currentUser.value?.departmentId || currentUser.value?.department_id || currentUser.value?.department?.id
  if (primaryId) {
    const primaryDept = departments.value.find(d => String(d.id) === String(primaryId))
    if (primaryDept) deptNames.add(primaryDept.name)
  }

  // 2. Add additional department names from the comma-separated string in the token
  const additional = currentUser.value?.departments
  if (additional && typeof additional === 'string' && additional.trim()) {
    additional.split(',').forEach(d => {
      const trimmed = d.trim()
      if (trimmed) deptNames.add(trimmed)
    })
  }

  return Array.from(deptNames)
})

// Temp list
const requestList = ref([])
const centralRequestList = ref([])

function switchRequestTab(tab) {
  if (tab !== 'store' && tab !== 'central') return
  activeRequestTab.value = tab
  if (tab === 'central' && !centralRequestingDepartments.value.length && !centralLoading.value) {
    loadCentralOptions()
  }
}

// Fetch (existing Store Request data — unchanged)
async function loadData() {
  if (!companyId.value && !branchId.value) return
  loading.value = true
  try {
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const [catRes, itemRes, depRes] = await Promise.all([
      apiFetch(`/categories?${query}`),
      apiFetch(`/items?${query}`),
      apiFetch(`/departments?${query}`)
    ])
    if (catRes.ok) categories.value = await catRes.json()
    categories.value = categories.value.filter(c => c.status === 1);
  categories.value.sort((a, b) => a.name.localeCompare(b.name))
    if (itemRes.ok) registeredItems.value = await itemRes.json()
    if (depRes.ok) departments.value = await depRes.json()
    departments.value = departments.value.filter(d => d.status === 1);
    departments.value.sort((a, b) => a.name.localeCompare(b.name))
  } catch (err) { console.error(err) }
  finally { loading.value = false }
}

/** Aggregated Transfer Issu (D) departments + scoped is_central categories from backend. */
async function loadCentralOptions() {
  if (!companyId.value && !branchId.value) {
    centralRequestingDepartments.value = []
    centralCategories.value = []
    return
  }
  centralLoading.value = true
  try {
    // Prefer branch scope like centralInventory / centralDepartmentPage
    const query = branchId.value
      ? `branchId=${branchId.value}`
      : `companyId=${companyId.value}`
    const res = await apiFetch(`/store-request/central-options?${query}`)
    if (!res.ok) {
      centralRequestingDepartments.value = []
      centralCategories.value = []
      return
    }
    const data = await res.json().catch(() => ({}))
    centralRequestingDepartments.value = Array.isArray(data?.requestingDepartments)
      ? data.requestingDepartments
      : []
    centralCategories.value = Array.isArray(data?.categories) ? data.categories : []
  } catch (err) {
    console.error('Error loading central request options:', err)
    centralRequestingDepartments.value = []
    centralCategories.value = []
  } finally {
    centralLoading.value = false
  }
}

/** Products + recipe ingredient names for selected central department (category). */
async function loadCentralItems(departmentId) {
  centralItems.value = []
  const depId = departmentId != null ? Number(departmentId) : NaN
  if (!Number.isFinite(depId) || depId <= 0) return
  centralLoading.value = true
  try {
    const res = await apiFetch(`/store-request/central-items/${depId}`)
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      console.error('Central items load failed:', err?.message || res.status)
      return
    }
    const data = await res.json().catch(() => [])
    centralItems.value = Array.isArray(data) ? data : []
  } catch (err) {
    console.error('Error loading central request items:', err)
    centralItems.value = []
  } finally {
    centralLoading.value = false
  }
}

function handleDeptChange() {
  if (!form.value.requestedDepartment) return
  // Find the departmentId matching the selected name from the globally loaded departments list
  const matched = departments.value.find(d => d.name === form.value.requestedDepartment)
  if (matched) {
    form.value.departmentId = matched.id
  }
}

function handleCentralDeptChange() {
  // Requesting department selection only stores id; label comes from list.
}

async function handleCentralCategoryChange(event) {
  const raw = event?.target?.value
  const parsed = raw === '' || raw == null ? NaN : Number(raw)
  const categoryId = Number.isFinite(parsed) && parsed > 0 ? parsed : null
  centralForm.value.categoryId = categoryId
  centralForm.value.itemKey = ''
  await loadCentralItems(categoryId)
}

// Actions
function processRequest() {
  if (isEditing.value) {
    requestList.value[editIndex.value] = { ...form.value }
    isEditing.value = false
    editIndex.value = -1
  } else {
    requestList.value.unshift({ ...form.value, requestedDepartment: form.value.requestedDepartment })
  }
  localSave()
  const currentHeader = { date: form.value.date, requestedDepartment: form.value.requestedDepartment, departmentId: form.value.departmentId }
  form.value = { ...getInitialForm(), ...currentHeader }
}

function editItem(index) {
  form.value = { ...requestList.value[index] }
  isEditing.value = true
  editIndex.value = index
}

function cancelEdit() {
  isEditing.value = false
  editIndex.value = -1
  const currentHeader = { date: form.value.date, requestedDepartment: form.value.requestedDepartment, departmentId: form.value.departmentId }
  form.value = { ...getInitialForm(), ...currentHeader }
}

function deleteItem(index) {
  requestList.value.splice(index, 1)
  localSave()
}

async function saveToDatabase() {
  if (!requestList.value.length) return
  isSaving.value = true
  try {
    const first = requestList.value[0]
    const payload = {
      date: first.date,
      companyId: companyId.value,
      branchId: branchId.value,
      created_by: userId.value,
      requestedDepartment: first.requestedDepartment,
      departmentId: first.departmentId,
      items: requestList.value.map(i => ({ itemId: i.itemId, quantity: i.quantity, remark: i.remark }))
    }
    const res = await apiFetch('/store-request', { method: 'POST', body: JSON.stringify(payload) })
    if (res.ok) {
      const responseData = await res.json().catch(() => ({}))
      const savedReqNo = responseData?.reqNo || responseData?.data?.reqNo || ''
      if (savedReqNo) {
        requestList.value = requestList.value.map((row) => ({ ...row, reqNo: savedReqNo }))
      }
      alert(savedReqNo ? `Store Request saved successfully. Request Number: ${savedReqNo}` : 'Store Request saved successfully.')
     
      // Trigger print before clearing the list
      setTimeout(async () => {
        if (confirm("Are you sure..........Print?")) {
          window.print()
        }
        requestList.value = []
        localStorage.removeItem('Mina_sr_temp')
        form.value = { ...getInitialForm() }
        // Refresh next SR number for the next entry.
        await fetchNextSrNumberForDisplay()
      }, 500)
    } else {
      const err = await res.json()
      alert('Error: ' + err.message)
    }
  } catch (err) {alert('Error: ' + err.message) }
  finally { isSaving.value = false }
}

function localSave() { localStorage.setItem('Mina_sr_temp', JSON.stringify(requestList.value)) }
function localLoad() {
    const saved = localStorage.getItem('Mina_sr_temp')
    if (saved) requestList.value = JSON.parse(saved)
}

function getItemName(id) { return registeredItems.value.find(i => i.id === id)?.itemName || '-' }
function getCategoryName(id) { return categories.value.find(c => c.id === id)?.name || '-' }

function getCentralDeptLabel(id) {
  return centralRequestingDepartments.value.find(d => String(d.id) === String(id))?.displayLabel || '-'
}
function getCentralCategoryName(id) {
  return centralCategories.value.find(c => String(c.id) === String(id))?.name || '-'
}
function getCentralItemByKey(itemKey) {
  if (!itemKey) return null
  return centralItems.value.find(i => `${i.type}_${i.id}` === itemKey) || null
}

/** Central Select Item label: "Name (unit Item)" or "Name (uom Pro)" */
function formatCentralItemOptionLabel(item) {
  if (!item) return ''
  const isProduct = String(item.type || '').toLowerCase() === 'product'
  const measure = String(isProduct ? (item.uom || '') : (item.unit || '')).trim()
  const kind = isProduct ? 'Pro' : 'Item'
  if (!measure) return `${item.name || ''} (${kind})`
  return `${item.name || ''} (${measure} ${kind})`
}

function processCentralRequest() {
  const selectedItem = getCentralItemByKey(centralForm.value.itemKey)
  if (!selectedItem) {
    alert('Please select an item.')
    return
  }
  const isProduct = String(selectedItem.type || '').toLowerCase() === 'product'
  const row = {
    ...centralForm.value,
    itemType: isProduct ? 'product' : 'item',
    itemId: isProduct ? null : selectedItem.id,
    productId: isProduct ? selectedItem.id : null,
    itemName: selectedItem.name,
  }
  if (isCentralEditing.value) {
    centralRequestList.value[centralEditIndex.value] = row
    isCentralEditing.value = false
    centralEditIndex.value = -1
  } else {
    centralRequestList.value.unshift(row)
  }
  const currentHeader = {
    date: centralForm.value.date,
    departmentId: centralForm.value.departmentId,
    categoryId: centralForm.value.categoryId,
  }
  centralForm.value = { ...getInitialCentralForm(), ...currentHeader, itemKey: '' }
}

async function editCentralItem(index) {
  const row = centralRequestList.value[index]
  if (!row) return
  const type = String(row.itemType || (row.productId ? 'product' : 'item')).toLowerCase()
  const id = type === 'product' ? (row.productId ?? row.itemId) : (row.itemId ?? row.productId)
  const itemKey = row.itemKey || `${type}_${id}`
  centralForm.value = {
    date: row.date,
    departmentId: row.departmentId,
    categoryId: row.categoryId,
    itemKey: '',
    quantity: row.quantity,
    remark: row.remark || '',
  }
  isCentralEditing.value = true
  centralEditIndex.value = index
  if (row.categoryId) {
    await loadCentralItems(row.categoryId)
  }
  centralForm.value.itemKey = itemKey
}

function cancelCentralEdit() {
  isCentralEditing.value = false
  centralEditIndex.value = -1
  const currentHeader = {
    date: centralForm.value.date,
    departmentId: centralForm.value.departmentId,
    categoryId: centralForm.value.categoryId,
  }
  centralForm.value = { ...getInitialCentralForm(), ...currentHeader, itemKey: '' }
}

function deleteCentralItem(index) {
  centralRequestList.value.splice(index, 1)
}

async function saveCentralToDatabase() {
  if (!centralRequestList.value.length) return
  isCentralSaving.value = true
  try {
    const first = centralRequestList.value[0]
    const requestedDepartment =
      getCentralDeptLabel(first.departmentId) !== '-'
        ? getCentralDeptLabel(first.departmentId)
        : ''
    const payload = {
      date: first.date,
      companyId: companyId.value,
      branchId: branchId.value,
      created_by: userId.value,
      requestedDepartment,
      departmentId: first.departmentId,
      items: centralRequestList.value.map((row) => {
        const isProduct = String(row.itemType || '').toLowerCase() === 'product' || !!row.productId
        if (isProduct) {
          return {
            itemId: null,
            productId: row.productId ?? row.itemId,
            quantity: row.quantity,
            remark: row.remark,
          }
        }
        return {
          itemId: row.itemId,
          productId: null,
          quantity: row.quantity,
          remark: row.remark,
        }
      }),
    }
    const res = await apiFetch('/store-request', { method: 'POST', body: JSON.stringify(payload) })
    if (res.ok) {
      const responseData = await res.json().catch(() => ({}))
      const savedReqNo = responseData?.reqNo || responseData?.data?.reqNo || ''
      alert(savedReqNo ? `Store Request saved successfully. Request Number: ${savedReqNo}` : 'Store Request saved successfully.')
      centralRequestList.value = []
      isCentralEditing.value = false
      centralEditIndex.value = -1
      centralForm.value = { ...getInitialCentralForm() }
      await fetchNextSrNumberForDisplay()
    } else {
      const err = await res.json().catch(() => ({}))
      alert('Error: ' + (err.message || 'Failed to save central request'))
    }
  } catch (err) {
    alert('Error: ' + (err?.message || 'Failed to save central request'))
  } finally {
    isCentralSaving.value = false
  }
}

async function fetchNextSrNumberForDisplay() {
  try {
    if (!companyId.value && !branchId.value) return
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const res = await apiFetch(`/store-request/next-number?${query}`)
    if (!res?.ok) return
    const data = await res.json().catch(() => ({}))
    const nextNumber = data?.nextNumber || ''
    // Display-only: do not use this value in save/update payloads.
    srNumberDisplay.value = nextNumber
  } catch (e) {
    console.error('Error fetching SR number for display:', e)
  }
}

onMounted(async () => {
  await loadData()
  localLoad()

  // Auto-select ONLY if exactly one department is assigned
  if (!form.value.requestedDepartment && userDeptList.value.length === 1) {
    form.value.requestedDepartment = userDeptList.value[0]
    handleDeptChange()
  }
  await fetchNextSrNumberForDisplay()
  // Prefetch central options so Central Request tab opens ready
  await loadCentralOptions()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.82rem;
  color: #1e293b;
}

th, td {
  padding: 5px 8px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.72rem;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

tbody tr:hover {
  background: #f1f5f9;
}

@media print {
  :global(.sidebar),
  :global(header),
  :global(nav),
  :global(.top-bar),
  :global(.app-header),
  :global(.logout-btn) {
    display: none !important;
  }

  .print-hide { display: none !important; }

  body, .bg-\[\#f8fafc\] {
    background: transparent !important;
  }

  .print-container {
    box-shadow: none !important;
    border: none !important;
  }

  .print-table {
    width: 100% !important;
    border-collapse: collapse !important;
    border-spacing: 0 !important;
  }

  .print-table th, .print-table td {
    border: 0 !important;
    border-right: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
    padding: 6px !important;
    text-align: left !important;
    color: #000 !important;
  }

  .print-table tr > *:first-child {
    border-left: 1px solid #000 !important;
  }

  .print-table thead tr:first-child > * {
    border-top: 1px solid #000 !important;
  }

  .print-table th {
    background-color: #f3f3f3 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .print-doc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  .print-doc-header-grid {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    column-gap: 12px;
  }

  .print-doc-left {
    justify-self: start;
  }

  .print-doc-title {
    justify-self: center;
    margin: 0 !important;
    white-space: nowrap;
  }

  .print-doc-right {
    justify-self: end;
  }

  .print-doc-title-wrap {
    justify-self: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }

  .print-company-name-title {
    font-size: 16px;
    text-align: center;
  }

  .print-company-block {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .print-company-logo {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    display: block;
    object-fit: contain;
    border: 0 !important;
    border-radius: 0;
    padding: 0;
    background: #fff;
  }

  .print-company-logo-fallback {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    border: 1px solid #000;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: #334155;
    background: #fff;
  }

  .print-company-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .print-company-name {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.2;
  }

  .print-doc-meta {
    font-size: 12px;
    color: #0f172a;
    white-space: nowrap;
  }
}


.editbtn {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.delbtn {
  background: none;
  margin-left: 10px;
  border: none;
  color: red;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}
</style>